import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { PlayIcon } from '@heroicons/react/24/outline';
import { AppEvent } from '../../types/event';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import AddToCalendarButton from '../events/AddToCalendarButton';
import { clockLine, eventBlurb, eventStart, eventTitle } from '../../utils/cardFacts';

export type EventCardLayout = 'list' | 'home' | 'row';

interface EventCardProps {
  event: AppEvent;
  layout: EventCardLayout;
  past?: boolean;
}

function DayBadge({ event, past, large }: { event: AppEvent; past: boolean; large: boolean }) {
  const start = eventStart(event);
  const tone = past ? 'bg-gray-100 text-gray-600' : 'bg-primary/10 text-primary';
  return (
    <div className={`${large ? 'w-20 h-20' : 'w-16 h-16'} rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${tone}`}>
      <span className={`${large ? 'text-2xl' : 'text-xl'} font-bold leading-none`}>{format(start, 'd')}</span>
      <span className="text-sm uppercase leading-none mt-1">{format(start, 'MMM')}</span>
    </div>
  );
}

export default function EventCard({ event, layout, past = false }: EventCardProps) {
  const title = eventTitle(event);
  const blurb = eventBlurb(event.excerpt || '');
  const row = layout === 'row';
  const home = layout === 'home';
  const showCalendar = layout === 'list';
  const timeLine = past && layout === 'list' ? '' : clockLine(event);
  const lines = [timeLine, row ? '' : (event.venue ?? '')].filter(Boolean);
  const to = `/events/${event.slug}`;
  const action = past && event.recordingUrl ? (
    <QuietAction to={`${to}?play=1`} label={`Watch recording ${title}`} icon={<PlayIcon className="h-4 w-4" aria-hidden />}>
      Watch recording
    </QuietAction>
  ) : (
    <QuietAction to={to} label={`View event ${title}`}>
      View event
    </QuietAction>
  );
  const day = (
    <div className="flex flex-col items-center flex-shrink-0">
      <DayBadge event={event} past={past} large={layout === 'list'} />
      {past && layout === 'list' && (
        <p className="mt-1 text-sm text-gray-600">{format(eventStart(event), 'yyyy')}</p>
      )}
    </div>
  );
  const copy = (
    <div className="min-w-0 flex-1">
      <div className={showCalendar ? 'relative' : undefined}>
        <h3 className={`${row ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>
          {showCalendar && <span className="float-right h-11 w-11" aria-hidden />}
          {title}
        </h3>
        {showCalendar && (
          <div className="absolute top-0 right-0">
            <AddToCalendarButton
              variant="corner"
              event={{
                title: event.title,
                description: event.excerpt || undefined,
                startDate: eventStart(event),
                endDate: event.endAt ? new Date(event.endAt) : null,
                isAllDay: event.allDay,
                location: event.venue || undefined,
                url: event.websiteUrl || undefined,
              }}
            />
          </div>
        )}
      </div>
      {blurb.topic && (
        <p className={`mt-2 text-sm text-gray-600 ${row ? 'line-clamp-1' : 'line-clamp-2'}`}>{blurb.topic}</p>
      )}
      {!row && blurb.presenter && <p className="mt-1 text-sm text-gray-600">{blurb.presenter}</p>}
      {lines.length > 0 && (
        <div className={row ? 'mt-1' : 'mt-2'}>
          {lines.map((line) => (
            <p key={line} className="text-sm text-gray-600">{line}</p>
          ))}
        </div>
      )}
      {!row && <div className="mt-4 flex justify-end">{action}</div>}
    </div>
  );
  const body = (
    <div className="flex items-start gap-4">
      {day}
      {copy}
    </div>
  );

  if (row) {
    return (
      <Link to={to} aria-label={title} className="block w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus-ring">
        {body}
      </Link>
    );
  }

  const card = <Card className="!p-4">{body}</Card>;
  if (home) return <div className="w-full max-w-xs">{card}</div>;
  return card;
}
